import React, { useCallback, useRef, useState } from "react";

type ResizableSplitLayoutProps = {
  defaultLeftPercent: number;
  minLeftPercent: number;
  maxLeftPercent: number;
  left: React.ReactNode;
  right: React.ReactNode;
};

export default function ResizableSplitLayout({
  defaultLeftPercent,
  minLeftPercent,
  maxLeftPercent,
  left,
  right
}: ResizableSplitLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPercent, setLeftPercent] = useState(defaultLeftPercent);
  const isDragging = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ev.clientX - rect.left;
        const pct = Math.min(
          maxLeftPercent,
          Math.max(minLeftPercent, (x / rect.width) * 100)
        );
        setLeftPercent(pct);
      };

      const onMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [minLeftPercent, maxLeftPercent]
  );

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 w-full overflow-hidden"
    >
      <div
        className="h-full min-h-0 min-w-0 flex-shrink-0 overflow-hidden"
        style={{ width: `${leftPercent}%` }}
      >
        {left}
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className="w-1 flex-shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 active:bg-blue-500 transition-colors"
        onMouseDown={handleMouseDown}
        role="separator"
        aria-orientation="vertical"
      />
      <div className="h-full min-h-0 min-w-0 flex-1 overflow-hidden">
        {right}
      </div>
    </div>
  );
}
